import { useEffect, useState } from 'react';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { StatusCard } from '@/components/ui/card/StatusCard';
import { useTranslation } from '@/hooks/useTranslation';
import type { AdminApiClient, AdminUser } from '@/features/admin/api/AdminApiClient';

export type DeleteMemberDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: AdminUser;
  client: AdminApiClient;
  onSuccess: () => void;
};

export function DeleteMemberDialog({ open, onOpenChange, member, client, onSuccess }: DeleteMemberDialogProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    const res = await client.deleteUser(member.email);
    setSubmitting(false);
    if (res.ok) {
      onSuccess();
      onOpenChange(false);
    } else {
      setError(res.message ?? t('administration.errors.generic'));
    }
  }

  return (
    // The dialog stays open on error so the message can be read and the deletion retried.
    <ConfirmDialog
      open={open}
      busy={submitting}
      title={t('administration.member.deleteTitle')}
      description={t('administration.member.deleteBody', { email: member.email })}
      confirmLabel={t('administration.member.confirmDelete')}
      cancelLabel={t('common.cancel')}
      onConfirm={() => void handleConfirm()}
      onCancel={() => onOpenChange(false)}
    >
      {error && <StatusCard type="error" message={error} />}
    </ConfirmDialog>
  );
}
